import { useForm, useFormState } from 'react-final-form';

import { Box, Icons, Stack, Typography } from '@cast/design-system';
import { useRffFormState } from '@cast/shared-rff-ui';

import { FIELD_NAMES, PRESET_TITLES, PRESET_VALUES } from '../_constants';
import { FormValues, PresetType } from '../_types';
import { PresetCard } from './PresetCard';
import { derivePreset } from '../_utils';

type PresetOption = {
  type: PresetType;
  title: string;
  description: string;
  icon: typeof Icons.Scales;
};

const PRESET_OPTIONS: PresetOption[] = [
  {
    type: 'cost-saving',
    title: PRESET_TITLES['cost-saving'],
    description: 'Maximum saving; Aggressive consolidation for all nodes.',
    icon: Icons.CurrencyDollar,
  },
  {
    type: 'balanced',
    title: PRESET_TITLES.balanced,
    description: 'Standard consolidation for all nodes to provide balance',
    icon: Icons.Scales,
  },
  {
    type: 'performance-first',
    title: PRESET_TITLES['performance-first'],
    description:
      'Minimal disruption; consolidation scoped to Cast managed nodes.',
    icon: Icons.ShieldCheck,
  },
  {
    type: 'custom',
    title: PRESET_TITLES.custom,
    description: 'Manually configured with custom behavior and modifiers',
    icon: Icons.Sliders,
  },
] as const;

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack gap={12}>
      <Stack gap={4}>
        <Typography variant="h6">Optimization profile</Typography>
        <Typography variant="P12R" color="textSecondary">
          Each preset configures the settings below
        </Typography>
      </Stack>
      <Box
        display="grid"
        gridTemplateColumns={`repeat(${PRESET_OPTIONS.length}, 1fr)`}
        gap={16}
      >
        {children}
      </Box>
    </Stack>
  );
};

export const OptimizationProfile = () => {
  const form = useForm<FormValues>();
  const { values: formValues } = useFormState<FormValues>();
  const { mode } = useRffFormState();
  const currentPreset = derivePreset(formValues);

  const handlePresetSelect = (presetType: PresetType) => {
    // Custom preset is not selectable. It is selected when user inputs values diviating from the preset values.
    if (presetType === 'custom') {
      return;
    }

    const presetValues = PRESET_VALUES[presetType];
    if (presetValues) {
      form.change(
        FIELD_NAMES.cycleIntervalSeconds,
        presetValues.cycleIntervalSeconds
      );
      form.change(
        FIELD_NAMES.nodeGracePeriodMinutes,
        presetValues.nodeGracePeriodMinutes
      );
      form.change(FIELD_NAMES.scopedMode, presetValues.scopedMode);
      form.change(
        FIELD_NAMES.maxTargetNodesPerCycle,
        presetValues.maxTargetNodesPerCycle
      );
      form.change(FIELD_NAMES.aggressiveMode, presetValues.aggressiveMode);
    }
  };

  return (
    <Layout>
      {PRESET_OPTIONS.map((presetOption) => {
        return (
          <PresetCard
            key={presetOption.type}
            icon={presetOption.icon}
            title={presetOption.title}
            description={presetOption.description}
            selected={presetOption.type === currentPreset}
            disabled={presetOption.type === 'custom' || mode === 'view'}
            onClick={() => handlePresetSelect(presetOption.type)}
            testId={`evictor-preset-${presetOption.type}`}
          />
        );
      })}
    </Layout>
  );
};

const OptimizationProfileSkeleton = () => {
  return (
    <Layout>
      {PRESET_OPTIONS.map((presetOption) => (
        <PresetCard.Skeleton key={presetOption.type} />
      ))}
    </Layout>
  );
};

OptimizationProfile.Skeleton = OptimizationProfileSkeleton;
